import React from "react";

import { useTranslation } from "react-i18next";

const Bar = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-gray-200 ${className}`} />
);

const IssuerBlockSkeleton = ({ showCredentialId = false }) => (
  <div className="flex flex-col gap-6">
    <div className="flex items-center gap-4">
      <Bar className="h-14 w-14 rounded-2xl" />
      <div className="flex flex-1 flex-col gap-2">
        <Bar className="h-3 w-16" />
        <Bar className="h-4 w-40" />
        <Bar className="h-3 w-28" />
      </div>
    </div>
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-[1.25rem]">
      <Bar className="h-3 w-20" />
      <Bar className="h-6 w-3/4" />
      <Bar className="h-6 w-44 rounded-full" />
      {showCredentialId ? (
        <div className="mt-1 flex items-center justify-between gap-3 border-t border-dashed border-gray-200 pt-[0.875rem]">
          <div className="flex flex-1 flex-col gap-2">
            <Bar className="h-3 w-24" />
            <Bar className="h-4 w-36" />
          </div>
          <Bar className="h-9 w-9 shrink-0" />
        </div>
      ) : null}
    </div>
  </div>
);

const BenefitsSkeleton = () => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
    {[0, 1, 2].map((item) => (
      <div key={item} className="flex items-start gap-3">
        <Bar className="h-10 w-10 shrink-0 rounded-xl" />
        <div className="flex flex-1 flex-col gap-2">
          <Bar className="h-3 w-20" />
          <Bar className="h-3 w-full" />
        </div>
      </div>
    ))}
  </div>
);

const StepsSkeleton = () => (
  <div className="flex flex-col gap-8">
    <div className="flex flex-col gap-2">
      <Bar className="h-5 w-72" />
      <Bar className="h-3 w-56" />
    </div>
    <div className="grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="flex flex-col gap-4">
          <div className="flex min-h-[4.75rem] items-start gap-3">
            <Bar className="h-7 w-7 shrink-0" />
            <div className="flex flex-1 flex-col gap-2">
              <Bar className="h-3 w-32" />
              <Bar className="h-3 w-full" />
              <Bar className="h-3 w-4/5" />
            </div>
          </div>
          <Bar className="mx-auto h-[340px] w-[168px] rounded-[28px]" />
        </div>
      ))}
    </div>
  </div>
);

const WalletOfferSkeleton = ({ isHandheld }) => {
  const { t } = useTranslation(["walletOffer"]);

  return (
    <div role="status" aria-live="polite" aria-busy="true" className="w-full">
      <span className="sr-only">{t("walletOffer:loading")}</span>
      {isHandheld ? (
        <div className="mx-auto flex w-full max-w-md flex-col gap-[1rem]">
          <div className="flex flex-col gap-6 rounded-3xl border border-gray-200 bg-white p-[1.25rem] shadow-sm sm:p-6">
            <IssuerBlockSkeleton showCredentialId />
            <div className="flex flex-col gap-3">
              <Bar className="h-[52px] w-full rounded-xl" />
              <Bar className="mx-auto h-3 w-56" />
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-[1rem] shadow-sm">
            <Bar className="h-11 w-11 shrink-0 rounded-xl" />
            <div className="flex flex-1 flex-col gap-2">
              <Bar className="h-3 w-28" />
              <Bar className="h-3 w-44" />
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-[minmax(0,1fr)_auto]">
            <div className="flex flex-col justify-between gap-4 p-6">
              <IssuerBlockSkeleton showCredentialId />
              <BenefitsSkeleton />
            </div>
            <div className="flex flex-col items-center justify-center gap-4 border-t border-gray-100 bg-gray-50 p-6 sm:p-8 md:border-l md:border-t-0 lg:px-12 lg:py-10">
              <Bar className="h-[268px] w-[268px] rounded-2xl lg:h-[296px] lg:w-[296px]" />
              <Bar className="h-4 w-40" />
              <Bar className="h-3 w-52" />
            </div>
          </div>
          <div className="border-t border-gray-100 p-6">
            <StepsSkeleton />
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletOfferSkeleton;
